import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { LocalizedText } from 'src/app/user-identification/models/user-identification';

/** Prefijo para persistir idioma por id de proceso (evita contaminación entre procesos). */
const PROCESS_LANG_STORAGE_PREFIX = 'pwcs_lang_';

/** Opciones de `resolveLocalizedText` (2.º argumento objeto). */
export interface ResolveLocalizedTextOptions {
	currentLang?: string;
	defaultLang?: string;
	fallbackText?: string;
	/** Default true. false = solo locale actual (FINISH custom NIF-762). */
	crossLocaleFallback?: boolean;
}

/**
 * El idioma por proceso se guarda en sessionStorage: cada pestaña tiene su copia y al refrescar
 * no gana el "último guardado" de otra pestaña (localStorage es compartido entre todas).
 * localStorage solo se lee una vez como migración desde versiones anteriores.
 */

@Injectable({
	providedIn: 'root',
})
export class LanguageService {
	private translateService = inject(TranslateService);
	private document = inject(DOCUMENT);
	private readonly languageChangedSubject = new Subject<string>();

	/** Emite cuando cambia el idioma efectivo aunque ngx-translate ya estuviera en ese locale. */
	readonly languageChanged$ = this.languageChangedSubject.asObservable();

	private defaultLanguage = 'es';
	private allowedLanguages = ['es', 'en', 'fr', 'pt', 'it'];
	private currentProcessId: string | null = null;

	public setCurrentProcessId(processId: string | null): void {
		this.currentProcessId = processId && String(processId).trim() !== '' ? String(processId) : null;
	}

	public setAllowedLanguages(languages: string[]): void {
		const normalized = (languages || []).map((lang) => String(lang).toLowerCase()).filter(Boolean);
		this.allowedLanguages = normalized.length ? Array.from(new Set(normalized)) : ['es', 'en', 'fr', 'pt', 'it'];
		if (!this.allowedLanguages.includes(this.defaultLanguage)) {
			this.defaultLanguage = this.allowedLanguages[0];
		}
	}

	private getProcessStorageKey(): string | null {
		return this.currentProcessId ? `${PROCESS_LANG_STORAGE_PREFIX}${this.currentProcessId}` : null;
	}

	/** Preferencia guardada para este proceso en esta pestaña; migra desde localStorage si existía. */
	private readStoredLanguageForProcess(key: string): string | null {
		const fromSession = sessionStorage.getItem(key);
		if (fromSession && this.allowedLanguages.includes(fromSession)) {
			return fromSession;
		}
		const fromLocal = localStorage.getItem(key);
		if (fromLocal && this.allowedLanguages.includes(fromLocal)) {
			sessionStorage.setItem(key, fromLocal);
			return fromLocal;
		}
		return null;
	}

	private writeStoredLanguageForProcess(key: string, lang: string): void {
		sessionStorage.setItem(key, lang);
	}

	/**
	 * Idioma efectivo: preferencia del proceso en storage, navegador o fallback.
	 * No usa getCurrentLang() de ngx-translate: su default (`lang: 'es'` en app.config)
	 * taparía el idioma del navegador en el arranque (NIF-1055).
	 */
	public getLanguage(): string {
		const key = this.getProcessStorageKey();
		if (key) {
			const stored = this.readStoredLanguageForProcess(key);
			if (stored) {
				return stored;
			}
		}

		return this.resolveLanguageWithoutStorage();
	}

	private resolveLanguageWithoutStorage(): string {
		const browserLang = this.getBrowserLanguage();
		if (this.allowedLanguages.includes(browserLang)) {
			return browserLang;
		}
		return this.defaultLanguage;
	}

	public setLanguage(lang: string): void {
		if (!this.allowedLanguages.includes(lang)) {
			console.log('lang ' + lang + ' is not in the allowed lang list');
			return;
		}

		const effectiveBefore = this.getLanguage();
		if (effectiveBefore === lang && this.translateService.getCurrentLang() === lang) {
			return;
		}

		const key = this.getProcessStorageKey();
		if (key) {
			this.writeStoredLanguageForProcess(key, lang);
		}
		this.document.documentElement.lang = lang;

		const notifyLanguageChanged = (): void => {
			this.languageChangedSubject.next(lang);
		};

		if (this.translateService.getCurrentLang() === lang) {
			notifyLanguageChanged();
			return;
		}

		this.translateService.use(lang).subscribe({
			next: notifyLanguageChanged,
		});
	}

	/**
	 * Arranque de la app antes de conocer el proceso: aplica idioma sin leer storage global legacy.
	 */
	public bootstrapLanguage(): void {
		const lang = this.getLanguage();
		this.document.documentElement.lang = lang;
		if (this.translateService.getCurrentLang() === lang) {
			return;
		}
		this.translateService.use(lang);
	}

	/** @deprecated Usar bootstrapLanguage antes del proceso o applyLanguageForCurrentProcess tras master. */
	public autoResolveLanguage(): void {
		this.bootstrapLanguage();
	}

	/**
	 * Tras cargar master y allowed languages: aplica idioma para el proceso actual y persiste la resolución inicial.
	 */
	public applyLanguageForCurrentProcess(): void {
		const lang = this.getLanguage();
		this.setLanguage(lang);
	}

	public getBrowserLanguage(): string {
		const lang = navigator.language || navigator.languages[0];
		return lang.split('-')[0];
	}

	public isLanguageAllowed(lang: string): boolean {
		return this.allowedLanguages.includes(lang);
	}

	public getAllowedLanguagesList(): string[] {
		return this.allowedLanguages;
	}

	/**
	 * Resuelve texto multi-locale.
	 * @param crossLocaleFallback Si false (p. ej. mensajes FINISH NIF-762), no cae a otro idioma:
	 *   solo el locale actual; si falta o está vacío → fallbackText / i18n del caller.
	 */
	public resolveLocalizedText(
		value?: LocalizedText | null,
		currentLangOrOptions: string | ResolveLocalizedTextOptions = this.getLanguage(),
		defaultLang: string = this.defaultLanguage,
		fallbackText: string = '',
	): string {
		let currentLang = this.getLanguage();
		let resolvedDefaultLang = defaultLang;
		let resolvedFallback = fallbackText;
		let crossLocaleFallback = true;

		if (typeof currentLangOrOptions === 'object' && currentLangOrOptions !== null) {
			currentLang = currentLangOrOptions.currentLang ?? this.getLanguage();
			resolvedDefaultLang = currentLangOrOptions.defaultLang ?? this.defaultLanguage;
			resolvedFallback = currentLangOrOptions.fallbackText ?? '';
			crossLocaleFallback = currentLangOrOptions.crossLocaleFallback ?? true;
		} else if (typeof currentLangOrOptions === 'string') {
			currentLang = currentLangOrOptions;
		}

		if (!value) return resolvedFallback;
		if (typeof value === 'string') return value;

		const normalizedCurrentLang = (currentLang || '').toLowerCase();
		const normalizedDefaultLang = (resolvedDefaultLang || this.defaultLanguage).toLowerCase();
		const normalized = Object.entries(value || {}).reduce(
			(acc, [key, text]) => {
				if (typeof text === 'string') {
					acc[key.toLowerCase()] = text;
				}
				return acc;
			},
			{} as Record<string, string>,
		);

		const byCurrent = normalized[normalizedCurrentLang];
		if (typeof byCurrent === 'string' && byCurrent.trim() !== '') {
			return byCurrent;
		}

		if (!crossLocaleFallback) {
			return resolvedFallback;
		}

		const byDefault = normalized[normalizedDefaultLang];
		if (byDefault) return byDefault;

		const firstAvailable = Object.values(normalized).find((text) => !!text);
		return firstAvailable || resolvedFallback;
	}
}
